"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getIndexName = void 0;
exports.toElasticQuery = toElasticQuery;
exports.mapSearchableAttributes = mapSearchableAttributes;
const schema_1 = require("../schema");
const extract_appropriate_types_1 = require("../utils/extract-appropriate-types");
const map_date_filter_1 = require("./filter-mapping/map-date-filter");
const map_has_nested_filter_1 = require("./filter-mapping/map-has-nested-filter");
const map_numeric_filter_1 = require("./filter-mapping/map-numeric-filter");
const map_string_filter_1 = require("./filter-mapping/map-string-filter");
const map_timestamp_filter_1 = require("./filter-mapping/map-timestamp-filter");
/**
 * Returns the index name for a given schema and environment. The master version of this is in @wayste/search-adapters.
 * The reason it is replicated here is to avoid a circular dependency.
 */
const getIndexName = (schema, environment) => `${schema.name}-${environment}`;
exports.getIndexName = getIndexName;
const splitSearchTerms = (searchQuery) => searchQuery.trim().split(/\s+/).filter(Boolean);
const groupSearchFields = (schema, searchFields) => {
    const searchFieldSet = new Set(searchFields);
    const groupedFields = schema.searchTermGroups
        ?.map((group) => group.map(String).filter((field) => searchFieldSet.has(field)))
        .filter((group) => group.length > 0) ?? [];
    const groupedFieldSet = new Set(groupedFields.flat());
    const ungroupedFields = searchFields.filter((field) => !groupedFieldSet.has(field)).map((field) => [field]);
    return [...groupedFields, ...ungroupedFields];
};
const filterIncludesValue = (filter, value) => {
    const filterValue = filter.value;
    if (Array.isArray(filterValue)) {
        return filterValue.includes(value);
    }
    return filterValue === value;
};
function toElasticQuery(query, { application, environment, customerID, haulerID, multiMatchType }) {
    const typeRestriction = query.type;
    if (application === 'wayste' && !haulerID) {
        throw new Error('Hauler ID must be provided for wayste search');
    }
    if (application === 'customer-portal' && !customerID) {
        throw new Error('Customer ID must be provided for customer portal search');
    }
    if (application === 'customer-portal' && query.type === 'sourgum-receivable') {
        query.filters = finalizeWQLFilters(query.filters, application, query.type);
    }
    if (Array.isArray(query.query?.restrictToFields) && query.query?.restrictToFields.length === 0) {
        // remove it
        delete query.query.restrictToFields;
    }
    if (typeRestriction) {
        const schema = schema_1.SearchSchema[typeRestriction];
        const elasticQuery = {
            index: (0, exports.getIndexName)(schema, environment),
            query: {
                bool: {
                    must: [],
                    filter: [],
                },
            },
        };
        if (query.query?.query) {
            const searchQuery = query.query.query;
            const baseSearchFields = query.query?.restrictToFields ||
                [...schema.searchableAttributes]
                    .filter((attribute) => attribute.searchableByDefault && !attribute.disabledOn?.includes(application))
                    .map((attribute) => attribute.field);
            const searchFields = mapSearchableAttributes(baseSearchFields);
            // Separate ngram fields from regular fields for better substring matching
            const ngramFields = searchFields.filter((field) => field.endsWith('.ngram'));
            const regularFields = searchFields.filter((field) => !field.endsWith('.ngram'));
            const shouldClauses = [];
            const buildSearchClauses = (searchText, fields = regularFields, fieldsWithNgram = ngramFields) => {
                const clauses = [];
                if (fields.length > 0) {
                    clauses.push({
                        multi_match: {
                            query: searchText,
                            fields,
                            type: multiMatchType ?? 'bool_prefix',
                        },
                    });
                }
                fieldsWithNgram.forEach((field) => {
                    clauses.push({
                        match: {
                            [field]: {
                                query: searchText,
                                analyzer: 'keyword',
                            },
                        },
                    });
                });
                return clauses;
            };
            if (!multiMatchType && schema.requireAllSearchTerms) {
                const searchTerms = splitSearchTerms(searchQuery);
                const groupedShouldClauses = groupSearchFields(schema, baseSearchFields).map((fieldGroup) => {
                    const mappedFieldGroup = mapSearchableAttributes(fieldGroup);
                    const groupNgramFields = mappedFieldGroup.filter((field) => field.endsWith('.ngram'));
                    const groupRegularFields = mappedFieldGroup.filter((field) => !field.endsWith('.ngram'));
                    return {
                        bool: {
                            must: searchTerms.map((searchTerm) => ({
                                bool: {
                                    should: buildSearchClauses(searchTerm, groupRegularFields, groupNgramFields),
                                    minimum_should_match: 1,
                                },
                            })),
                        },
                    };
                });
                elasticQuery.query = {
                    bool: {
                        should: groupedShouldClauses,
                        minimum_should_match: 1,
                        filter: [],
                    },
                };
            }
            else {
                shouldClauses.push(...buildSearchClauses(searchQuery));
                const bool = {
                    should: shouldClauses,
                    minimum_should_match: 1,
                    filter: [],
                };
                elasticQuery.query = { bool };
            }
        }
        if (query.filters && query.filters.length > 0) {
            const filters = query.filters
                // @ts-expect-error -- ts is right this is impossible, but we need to filter out nulls because frontend
                .filter((filter) => filter.value !== null || filter.value !== undefined)
                .map((filter) => {
                switch (filter.type) {
                    case 'STRING':
                        return (0, map_string_filter_1.mapStringFilter)(filter);
                    case 'NUMBER':
                        return (0, map_numeric_filter_1.mapNumericFilter)(filter);
                    case 'BOOLEAN':
                        return {
                            term: {
                                [filter.field]: filter.value,
                            },
                        };
                    case 'HAS_NESTED': {
                        return (0, map_has_nested_filter_1.mapHasNestedFilter)(filter, schema);
                    }
                    case 'TIMESTAMP':
                        return (0, map_timestamp_filter_1.mapTimestampFilter)(filter);
                    case 'DATE':
                        return (0, map_date_filter_1.mapDateFilter)(filter);
                    default: {
                        const filterAny = filter;
                        console.error('Unhandled filter type', filterAny?.type);
                        return undefined;
                    }
                }
            })
                .filter((filter) => filter !== null && filter !== undefined && Object.keys(filter).length > 0);
            elasticQuery.query.bool.filter = filters;
        }
        if (query.sort) {
            // Find the attribute to determine its type
            const sortAttribute = [...schema.filterableAttributes].find((attribute) => attribute.field === query.sort?.field);
            // If it's a string field, use .keyword for sorting unless the mapping is already a keyword field.
            const sortField = sortAttribute?.type === 'STRING' && sortAttribute.useKeywordSubfield !== false
                ? `${query.sort.field}.keyword`
                : query.sort.field;
            elasticQuery.sort = [
                {
                    [sortField]: {
                        order: mapSortDirectionToElasticsearchDirection(query.sort.direction),
                        // Docs missing the field sort on Long.MIN_VALUE, which `strict_date_time` can't print and fails the search.
                        ...(sortAttribute?.type === 'TIMESTAMP' && { format: 'strict_date_optional_time' }),
                    },
                },
            ];
            if (query.sort.needsDeterministicSortingAdded || sortAttribute?.needsDeterministicSorting) {
                elasticQuery.sort.push('_doc');
            }
        }
        if (haulerID && Array.isArray(elasticQuery.query?.bool?.filter)) {
            elasticQuery.query.bool.filter.push({
                term: {
                    'haulerID.keyword': haulerID,
                },
            });
        }
        else if (application === 'wayste') {
            throw new Error('Hauler ID must be provided for wayste search');
        }
        const explicitlySearchingDecommissionedContainers = typeRestriction === 'wayste-tagged-container' &&
            query.filters?.some((filter) => filter.field === 'status' &&
                filter.type === 'STRING' &&
                ['IS_EQUAL_TO', 'IS_IN'].includes(filter.operator) &&
                filterIncludesValue(filter, 'DECOMMISSIONED'));
        if (typeRestriction === 'wayste-tagged-container' && !explicitlySearchingDecommissionedContainers) {
            elasticQuery.query.bool.must_not = [
                {
                    term: {
                        'status.keyword': 'DECOMMISSIONED',
                    },
                },
            ];
        }
        if (application === 'customer-portal' && Array.isArray(elasticQuery.query?.bool?.filter)) {
            elasticQuery.query.bool.filter.push({
                bool: {
                    should: [
                        {
                            term: {
                                'allianceCustomerID.keyword': customerID,
                            },
                        },
                        {
                            term: {
                                'customerID.keyword': customerID,
                            },
                        },
                    ],
                },
            });
        }
        else if (application === 'customer-portal') {
            throw new Error('Customer ID must be provided for customer portal search');
        }
        if (['aap', 'customer-portal'].includes(application) &&
            typeRestriction === 'customer' &&
            Array.isArray(elasticQuery.query?.bool?.filter)) {
            elasticQuery.query.bool.filter.push({
                term: {
                    'haulerID.keyword': '99999999-9999-9999-9999-999999999999',
                },
            });
        }
        // Request runtime fields explicitly so they are computed and returned in the response
        // Runtime fields have a 'script' property in their elasticMapping
        const runtimeFields = schema.extraMappings
            ?.filter((mapping) => 'elasticMapping' in mapping && 'script' in mapping.elasticMapping)
            .map((mapping) => mapping.field);
        if (runtimeFields && runtimeFields.length > 0) {
            elasticQuery.fields = runtimeFields;
        }
        return elasticQuery;
    }
    // If no type restriction is given, we search all types
    // Note: Filters are only processed when there's a type restriction
    const allowedSchemas = (0, extract_appropriate_types_1.extractAllowedSchemas)(application);
    const allSearchableAttributes = allowedSchemas
        .map((schema) => schema.searchableAttributes)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .reduce((acc, searchableAttributes) => {
        return [...acc, ...searchableAttributes];
    }, []);
    const elasticQuery = {
        index: allowedSchemas.map((schema) => (0, exports.getIndexName)(schema, environment)),
        size: 0,
        query: {
            bool: {
                must: [
                    {
                        multi_match: {
                            query: query.query?.query || '',
                            fields: mapSearchableAttributes(query.query?.restrictToFields ||
                                [...allSearchableAttributes]
                                    .filter((attribute) => attribute.searchableByDefault && !attribute.disabledOn?.includes(application))
                                    .map((attribute) => attribute.field)),
                            type: multiMatchType ?? 'bool_prefix',
                        },
                    },
                ],
                filter: [],
            },
        },
        aggs: {
            index_agg: {
                terms: {
                    field: '_index',
                    size: 20,
                },
                aggs: {
                    hits_per_index: {
                        top_hits: {
                            size: 3,
                        },
                    },
                },
            },
        },
    };
    if (haulerID && Array.isArray(elasticQuery.query?.bool?.filter)) {
        elasticQuery.query.bool.filter.push({
            term: {
                'haulerID.keyword': haulerID,
            },
        });
    }
    else if (application === 'wayste') {
        throw new Error('Hauler ID must be provided for wayste search');
    }
    if (application === 'customer-portal' && Array.isArray(elasticQuery.query?.bool?.filter)) {
        elasticQuery.query.bool.filter.push({
            bool: {
                should: [
                    {
                        term: {
                            'allianceCustomerID.keyword': customerID,
                        },
                    },
                    {
                        term: {
                            'customerID.keyword': customerID,
                        },
                    },
                ],
            },
        });
        if (!query.type) {
            elasticQuery.query.bool.filter.push({
                term: {
                    'invoiceDetails.void': false,
                },
            }, {
                bool: {
                    must_not: {
                        terms: {
                            'invoiceDetails.status.keyword': ['DRAFT'],
                        },
                    },
                },
            });
        }
    }
    else if (application === 'customer-portal') {
        throw new Error('Customer ID must be provided for customer portal search');
    }
    // Apply haulerID filter for customer index in multi-index searches for specific applications
    if (['aap', 'customer-portal'].includes(application) && Array.isArray(elasticQuery.query?.bool?.filter)) {
        elasticQuery.query.bool.filter.push({
            bool: {
                should: [
                    {
                        // For customer index: require haulerID filter
                        bool: {
                            must: [
                                { term: { _index: `customer-${environment}` } },
                                { term: { 'haulerID.keyword': '99999999-9999-9999-9999-999999999999' } },
                            ],
                        },
                    },
                    {
                        // For non-customer indices: no additional restriction
                        bool: {
                            must_not: [{ term: { _index: `customer-${environment}` } }],
                        },
                    },
                ],
                minimum_should_match: 1,
            },
        });
    }
    return elasticQuery;
}
function mapSearchableAttributes(attributes) {
    return attributes.reduce((acc, attribute) => {
        acc.push(attribute);
        acc.push(`${attribute}._2gram`);
        acc.push(`${attribute}._3gram`);
        // Also include .ngram field for indices that use ngram subfield (e.g., crm-site)
        acc.push(`${attribute}.ngram`);
        // For status field, also include the text subfield for partial matching
        if (attribute === 'status') {
            acc.push(`${attribute}.text`);
        }
        return acc;
    }, []);
}
function mapSortDirectionToElasticsearchDirection(direction) {
    switch (direction) {
        case 'ASC':
            return 'asc';
        case 'DESC':
            return 'desc';
        default:
            throw new Error(`Invalid sort direction: ${direction}`);
    }
}
function finalizeWQLFilters(filters, application, type) {
    // FIXME: application specific filters should be installed by the application itself
    if (application === 'customer-portal' && type === 'sourgum-receivable') {
        const sgReceivable = filters;
        sgReceivable.push({ field: 'invoiceDetails.void', operator: 'IS_EQUAL_TO', type: 'BOOLEAN', value: false });
        const found = sgReceivable?.find((item) => item.field === 'invoiceDetails.status');
        if (!found) {
            return [
                ...sgReceivable,
                { field: 'invoiceDetails.status', operator: 'IS_NOT_IN', type: 'STRING', value: ['DRAFT'] },
            ];
        }
        if (found && (found.operator === 'IS_NOT_EQUAL_TO' || found.operator === 'IS_NOT_IN')) {
            return [
                ...sgReceivable,
                {
                    ...found,
                    operator: 'IS_NOT_IN',
                    // @ts-expect-error -- found would be an array
                    value: found.operator === 'IS_NOT_EQUAL_TO' ? [found.value, 'DRAFT'] : [...found.value, 'DRAFT'],
                },
            ];
        }
    }
    return filters;
}
//# sourceMappingURL=elastic-query.js.map